// Assembly della sequenza di schermate per un prodotto.
//
// I 3 modi di definire quali schermate mostrare, senza limitarsi a vicenda:
//   Modo 1 — Product.type: il tipo del prodotto forza la propria schermata
//            (device-data / consent / photo-feedback) in testa alla sequenza.
//   Modo 2 — EventScreenOrder: sequenza ordinata per-evento (dal backend).
//   Modo 3 — EventTypeScreenOrder: sequenza ordinata per-event-type (dal backend).
//
// Il backend (`/event-screens/`) serve gia' i Modi 2/3 (event-type ha priorita'
// sull'event). Qui applichiamo il Modo 1 facendo un MERGE con la sequenza fetchata:
// la schermata del type viene prepended in testa, ma solo se non e' gia' presente
// nella sequenza dell'evento/event-type (niente duplicati). Cosi' i 3 modi
// coesistono invece di escludersi.

// Mappa: Product.type -> screen key nel catalogo ScreenType.
// NB: il type usa underscore (device_data), lo screen key usa il trattino (device-data).
const TYPE_TO_SCREEN_KEY: Record<string, string> = {
  images: 'photo-feedback',
  consent: 'consent',
  device_data: 'device-data',
};

/**
 * Restituisce la schermata forzata dal type del prodotto (Modo 1), o null se il
 * type non ne ha una associata (standard / geo / no_audio, ...).
 */
export function screenKeyForProductType(type?: string | null): string | null {
  if (!type) return null;
  return TYPE_TO_SCREEN_KEY[type] ?? null;
}

/**
 * Fonde la sequenza scaricata dal backend (Modo 2/3) con la schermata del type
 * del prodotto (Modo 1). La schermata del type finisce in testa; se e' gia'
 * presente nella sequenza dell'evento non viene duplicata.
 *
 * Eccezione — ingresso per evento (`?event=<code>`, flusso multiquestion):
 * l'ordine dell'evento/event-type comanda da solo, esattamente come per tutti
 * gli altri event/event-type. In quel caso il prodotto in context e' solo il
 * primo per Product.order (serve a stile/branding e a far partire l'iterazione
 * sul feedback), quindi il suo type NON deve scavalcare il primo screen per
 * order: se l'evento ha consent in testa si parte da consent, poi feedback, e
 * da li' valgono le solite logiche di prodotto. Il Modo 1 resta attivo per gli
 * ingressi per prodotto (`?product=<code>`), inclusi gli hop successivi della
 * catena multiquestion.
 */
export function buildScreenSequence(
  fetchedScreens: string[],
  product?: { type?: string | null } | null,
  options?: { isEventEntry?: boolean }
): string[] {
  const fetched = Array.isArray(fetchedScreens) ? fetchedScreens : [];
  if (options?.isEventEntry) return fetched;
  const typeScreen = screenKeyForProductType(product?.type);
  if (!typeScreen) return fetched;
  // Gia' in sequenza (in qualsiasi posizione) -> non prependere, evita doppioni.
  if (fetched.includes(typeScreen)) return fetched;
  return [typeScreen, ...fetched];
}

/**
 * Indice dello screen di feedback dentro una sequenza, per un dato prodotto.
 * Per i prodotti 'images' il feedback e' photo-feedback (stessa regola usata da
 * nextScreen per riconoscere lo step di feedback). -1 se la sequenza non ne ha.
 */
export function feedbackScreenIndex(
  sequence: string[],
  product?: { type?: string | null } | null
): number {
  if (product?.type === 'images') {
    const photoIndex = sequence.indexOf('photo-feedback');
    if (photoIndex !== -1) return photoIndex;
  }
  return sequence.indexOf('feedback');
}

// Screen di RISPOSTA: gli unici in cui l'utente risponde alla domanda del
// prodotto. Solo qui hanno senso le proprieta' del prodotto legate alla domanda
// (product_indication, product_img, configurazione multianswer): sugli altri
// screen della sequenza (consent, device-data, photo-feedback, registration,
// codescanner, ...) la domanda non c'entra nulla e l'header deve restare quello
// standard. Vedi Header.tsx (unico consumer) e isAnswerScreen qui sotto.
//
// NB: photo-feedback resta FUORI anche per i prodotti 'images', per cui e' di
// fatto lo screen di feedback (vedi feedbackScreenIndex). E' uno step di
// raccolta foto: la domanda appartiene allo screen 'feedback' e non va mostrata
// sopra al caricamento immagini. PhotoFeedbackScreen continua comunque a
// inviare `question` al backend: cambia solo cosa si vede.
const ANSWER_SCREEN_KEYS = new Set(['feedback', 'feedback-hold', 'stars']);

/**
 * True se lo screen key e' uno screen di risposta, cioe' uno di quelli che
 * mostrano la domanda del prodotto. null/undefined (screen non ancora
 * risolto, es. durante il fetch della sequenza) -> false: la domanda non
 * compare finche' non sappiamo di essere su uno screen di risposta.
 */
export function isAnswerScreen(screenKey?: string | null): boolean {
  return !!screenKey && ANSWER_SCREEN_KEYS.has(screenKey);
}
